import React from 'react'
import dayjs from 'dayjs'
import { Controller, type UseFormReturn } from 'react-hook-form'

import { Box, Stack, Typography } from '@mui/material'

import { TextField, SelectField, SelectCountryField, ProfilePhotoField, DatePickerField } from 'components'

import { FULL_NAME_RULES, EMAIL_RULES, PHONE_RULES, GENDER_OPTIONS, textFieldHeightSx, datePickerHeightSx } from './ownerFormShared'

// VERA-914 (Stage B) — Details section, shared verbatim between Create Owner
// and Edit Owner so the two forms can never drift. Photo/Full Name/Email/
// Phone/Gender existed before this ticket; Country is new (Details section).
// Birthday replaces the old derived-from-age field — `defaultBirthday` is read
// directly from the loaded owner record (not `methods.getValues()`) so the
// DatePickerField's own uncontrolled state initializes correctly at mount on
// Edit (mirrors ProductEditPage's `defaultValue={data?.manufactured_at}`
// pattern — the `useEffect`-driven `reset()` in OwnerEditPage runs one render
// after mount, too late for this component's internal `useState`).
export const OwnerDetailsFields = ({
  methods,
  defaultBirthday,
}: {
  methods: UseFormReturn<any>
  defaultBirthday?: string | null
}) => {
  const { control, watch } = methods

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <Controller
        name="photo"
        control={control}
        render={({ field, fieldState }) => (
          <ProfilePhotoField
            label="Profile Photo"
            value={field.value}
            onChange={(file) => field.onChange(file)}
            defaultInitials={watch('full_name')?.charAt(0) || 'U'}
            error={fieldState.error?.message}
            uploadLabel="Upload"
            changeLabel="Change"
          />
        )}
      />

      <Box sx={textFieldHeightSx}>
        <Controller
          name="full_name"
          control={control}
          rules={FULL_NAME_RULES}
          render={({ field, fieldState }) => (
            <TextField
              {...methods}
              label="Full Name"
              name="full_name"
              labelVariant="body2"
              value={field.value === undefined || field.value === null ? '' : String(field.value)}
              onValueChange={(value) => field.onChange(value)}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
        <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: 220, ...textFieldHeightSx }}>
          <Controller
            name="email"
            control={control}
            rules={EMAIL_RULES}
            render={({ field, fieldState }) => (
              <TextField
                {...methods}
                label="Email"
                name="email"
                type="email"
                labelVariant="body2"
                value={field.value === undefined || field.value === null ? '' : String(field.value)}
                onValueChange={(value) => field.onChange(value)}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Box>
        <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: 220, ...textFieldHeightSx }}>
          <Controller
            name="phone"
            control={control}
            rules={PHONE_RULES}
            render={({ field, fieldState }) => (
              <TextField
                {...methods}
                label="Phone"
                name="phone"
                labelVariant="body2"
                value={field.value === undefined || field.value === null ? '' : String(field.value)}
                onValueChange={(value) => field.onChange(value)}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
        <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 200, ...datePickerHeightSx }}>
          <Controller
            name="birthday"
            control={control}
            render={({ field }) => (
              <DatePickerField
                label="Birthday"
                helperText="optional"
                name="birthday"
                defaultValue={defaultBirthday ?? undefined}
                onValueChange={(value) => field.onChange(value ?? '')}
                format="MM/DD/YYYY"
                views={['year', 'month', 'day']}
                openTo="year"
                disableFuture
                maxDate={dayjs()}
                {...methods}
                {...field}
              />
            )}
          />
        </Box>

        <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 200 }}>
          <Controller
            name="gender"
            control={control}
            render={({ field, fieldState }) => (
              <Box>
                <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: '8px' }}>
                  <Typography variant="body2">Gender</Typography>
                  <Typography variant="body4" color="text.secondary">(optional)</Typography>
                </Stack>
                <SelectField
                  placeholder="Select gender..."
                  value={field.value || ''}
                  onChange={(value) => field.onChange(value as string)}
                  options={GENDER_OPTIONS}
                />
                {fieldState.error?.message && (
                  <Box sx={{ mt: 1, fontSize: '0.75rem', color: 'error.main' }}>
                    {fieldState.error.message}
                  </Box>
                )}
              </Box>
            )}
          />
        </Box>

        <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 200 }}>
          <Controller
            name="country"
            control={control}
            render={({ field, fieldState }) => (
              <Box>
                <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: '8px' }}>
                  <Typography variant="body2">Country</Typography>
                  <Typography variant="body4" color="text.secondary">(optional)</Typography>
                </Stack>
                <SelectCountryField
                  value={field.value || ''}
                  onChange={(code) => field.onChange(code)}
                  error={fieldState.error}
                />
              </Box>
            )}
          />
        </Box>
      </Box>
    </Box>
  )
}
